import { UserRegistration } from 'src/app/user/model/user-registration';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RegistrationService } from 'src/app/core/service/registration.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder, ValidationErrors, ValidatorFn, UntypedFormControl } from '@angular/forms';
import { UserValidators } from 'src/app/core/validators/user.validators';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserSetting } from '../model/user-setting';
import { LanguageService } from 'src/app/core/service/language.service';

@Component({
  selector: 'app-user-confirm',
  templateUrl: './user-confirm.component.html',
  styleUrls: ['./user-confirm.component.scss']
})
export class UserConfirmComponent implements OnInit
{
  /**
   * Valid Token.
   */
  public validToken = false;

  /**
   * Validating token.
   */
  public validating = true;

  /**
   * Token.
   */
  public registerUser: UserRegistration;
  public forgotPassword = false;
  public disabledUserName = false;
  public userForm: any = null;
  public forgotPasswordForm: any = null;
  public userSetting: UserSetting;
  public model = new UserRegistration();
  public qrCodeDisplayImage = false;
  public qrCodeImageUrl = null;
  public qrCodeKey = null;
  public allowAlternativeTwoStepAuth = false;

  // Username and Password restrictions
  public minUsernameLength: number;
  public maxUsernameLength: number;
  public usernamePattern: string;
  public minPasswordLength: number;
  public maxPasswordLength: number;
  public passwordPattern: string;
  public passwordMeterColors: any[];
  public invalidPasswordText = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userValidators: UserValidators,
    private route: ActivatedRoute,
    private messageHandlerService: MessageHandlerService,
    private registrationService: RegistrationService,
    private userService: UserService,
    private settingService: SettingService,
    private languageService: LanguageService,
    private router: Router
  )
  {
    // Set password meter colors
    this.passwordMeterColors = this.userService.getPasswordMeterColors();
  }

  public get username(): UntypedFormControl
  {
    return this.userForm.get('username');
  }

  public get password(): UntypedFormControl
  {
    return this.userForm.get('password');
  }

  public get passwordConfirm(): UntypedFormControl
  {
    return this.userForm.get('passwordConfirm');
  }

  public get passwordReset(): UntypedFormControl
  {
    return this.forgotPasswordForm.get('passwordReset');
  }

  public get passwordResetConfirm(): UntypedFormControl
  {
    return this.forgotPasswordForm.get('passwordResetConfirm');
  }

  public ngOnInit(): void
  {
    this.qrCodeImageUrl = null;
    this.qrCodeKey = null;
    this.qrCodeDisplayImage = false;
    this.disabledUserName = false;
    this.route.params.forEach((params: Params) =>
    {
      if (params.token !== undefined)
      {
        // Set model
        this.model.token = params.token;
        this.getUserSettings();
        this.getSessionSettings();
      }
    });

    this.route.url.forEach(element =>
    {
      element.forEach(segment =>
      {
        if (segment.path === 'forgotpassword')
        {
          this.forgotPassword = true;
        }
      });
    });
  }

  public formValid(): boolean
  {
    return this.userForm.valid;
  }

  public forgotPasswordFormValid(): boolean
  {
    return this.forgotPasswordForm.valid;
  }

  public formDirty(): boolean
  {
    return this.userForm.dirty;
  }

  public forgotPasswordFormDirty(): boolean
  {
    return this.forgotPasswordForm.dirty;
  }

  public getRegistrationTitle(): string
  {
    if (this.qrCodeDisplayImage)
    {
      return 'REGISTRATION-COMPLETED-QR-CODE-COMPLETION';
    }
    else
    {
      return this.disabledUserName ? 'COMPLETE-REGISTRATION-TITLE-PASSWORD' : 'COMPLETE-REGISTRATION-TITLE';
    }
  }

  public register(): void
  {
    this.model.username = this.userForm.controls.username.value;
    this.model.password = this.userForm.controls.password.value;

    this.registrationService.registerUser(this.model).subscribe({
      next: (data) =>
      {
        if (data)
        {
          if (!data.valid)
          {
            this.messageHandlerService.displayError('TOKEN-EXPIRED');
          }
          else
            if (data.registered)
            {
              this.messageHandlerService.displaySuccess('USER-SUCCESSFULLY-REGISTERED');
              // If two factor auth is enabled for the user, on registration we need to show the QR Code to the user.
              if (data.qrCode !== null)
              {
                this.qrCodeImageUrl = data.qrCode.qrCodeImage;
                this.qrCodeKey = data.qrCode.key;
                this.qrCodeDisplayImage = true;
              }
              else
              {
                setTimeout(() => { this.redirectToLogin(); }, 3000);
              }
            }
            else
            {
              this.messageHandlerService.displayError('UNPREDICTABLE-REGISTRATION-ERROR');
            }
        }
        else
        {
          this.messageHandlerService.displayError('UNPREDICTABLE-REGISTRATION-ERROR');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public resetPassword(): void
  {
    this.model.password = this.forgotPasswordForm.controls.passwordReset.value;

    this.registrationService.resetPassword(this.model).subscribe({
      next: (data) =>
      {
        if (data)
        {
          if (!data.valid)
          {
            this.messageHandlerService.displayError('TOKEN-EXPIRED');
          }
          else
            if (data.registered)
            {
              this.messageHandlerService.displaySuccess('PASSWORD-SUCCESSFULLY-REGISTERED');
              setTimeout(() => { this.redirectToLogin(); }, 3000);
            }
            else
            {
              this.messageHandlerService.displayError('UNPREDICTABLE-RESET-ERROR');
            }
        }
        else
        {
          this.messageHandlerService.displayError('UNPREDICTABLE-RESET-ERROR');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public redirectToLogin(): void
  {
    this.router.navigate(['login']);
  }

  private buildForm(): void
  {
    // Set password validators
    const passwordValidators = [
      Validators.required,
      Validators.minLength(this.minPasswordLength),
      Validators.pattern(this.passwordPattern)
    ];

    // Set username validators
    const usernameValidators = [
      Validators.required,
      Validators.minLength(this.minUsernameLength),
      Validators.pattern(this.usernamePattern)
    ];

    // Set username async validators
    const usernameAsyncValidators = [
      this.userValidators.validateUsernameUnique.bind(this)
    ];

    // Set form controls
    this.userForm = this.formBuilder.group({
      username: [
        { value: null, disabled: false },
        {
          validators: usernameValidators,
          asyncValidators: usernameAsyncValidators
        }
      ],
      password: [
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            Validators.minLength(this.minPasswordLength),
            Validators.pattern(this.passwordPattern)
          ],
        }
      ],
      passwordConfirm: [null]
    });

    // Set forgot password form controls
    this.forgotPasswordForm = this.formBuilder.group({
      passwordReset: [
        { value: null, disabled: false },
        {
          validators: [
            Validators.required,
            Validators.minLength(this.minPasswordLength),
            Validators.pattern(this.passwordPattern)
          ],
        }
      ],
      passwordResetConfirm: [null],
      token: [this.model.token, Validators.required]
    });

    const passwordConfirmValidators = passwordValidators.splice(0);
    passwordConfirmValidators.push(this.passwordMatchValidator());

    const forgotPasswordConfirmValidators = [];
    forgotPasswordConfirmValidators.push(this.forgotPasswordMatchValidator());

    this.userForm.controls.passwordConfirm.setValidators(passwordConfirmValidators);
    this.userForm.controls.passwordConfirm.updateValueAndValidity();

    this.userForm.get('password').valueChanges.subscribe({
      next: () =>
      {
        this.userForm.get('passwordConfirm').updateValueAndValidity();
      }
    });

    this.forgotPasswordForm.controls.passwordResetConfirm.setValidators(forgotPasswordConfirmValidators);
    this.forgotPasswordForm.controls.passwordResetConfirm.updateValueAndValidity();

    this.forgotPasswordForm.get('passwordReset').valueChanges.subscribe({
      next: () =>
      {
        this.forgotPasswordForm.get('passwordResetConfirm').updateValueAndValidity();
      }
    });

    this.registrationService.validateRegistrationToken(this.model).subscribe({
      next: (tokenData) =>
      {
        this.validToken = tokenData && tokenData.token === this.model.token && tokenData.valid;
        // If there is a username set use it and disable changes.
        if (tokenData.username)
        {
          this.username.setValue(tokenData.username);
          this.username.disable();
          this.disabledUserName = true;
        }
        else if (!this.validToken)
        {
          setTimeout(() => { this.redirectToLogin(); }, 5000);
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.validating = false;
      }
    });
  }

  private passwordMatchValidator(): ValidatorFn
  {
    return (control: UntypedFormGroup): ValidationErrors | null =>
    {
      const password = this.password.value ? this.password.value : '';
      const passwordConfirm = control.value ? control.value : '';
      if (password !== passwordConfirm)
      {
        return {
          unmatchConfirmPassword: true
        };
      }
      return null;
    };
  }

  private forgotPasswordMatchValidator(): ValidatorFn
  {
    return (control: UntypedFormGroup): ValidationErrors | null =>
    {
      const password = this.passwordReset.value ? this.passwordReset.value : '';
      const passwordConfirm = control.value ? control.value : '';
      if (password !== passwordConfirm)
      {
        return {
          unmatchConfirmPassword: true
        };
      }
      return null;
    };
  }

  private getUserSettings()
  {
    this.settingService.getCachedUserSettings().subscribe({
      next: (data) =>
      {
        this.userSetting = data;

        // Set username, password and image size from settings
        this.minUsernameLength = this.userSetting.usernameMin;
        this.maxUsernameLength = this.userSetting.usernameMax;
        this.usernamePattern = this.userSetting.usernamePattern;
        this.minPasswordLength = this.userSetting.passwordMin;
        this.maxPasswordLength = this.userSetting.passwordMax;
        this.passwordPattern = this.userSetting.passwordPattern;

        this.buildForm();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getSessionSettings(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        // Get Password Pattern Text
        const languageIndex = this.languageService.currentLanguage === 'es' ? 1 :
          this.languageService.currentLanguage === 'de' ? 2 : 0;
        this.invalidPasswordText = sessionSetting.passwordPatternTexts[languageIndex];
        this.allowAlternativeTwoStepAuth = sessionSetting.allowAlternativeTwoStepAuthEnabled;
      }
    });
  }
}
